import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFutbol } from '@fortawesome/free-solid-svg-icons';
import { GET_SHL_SPORTS } from '../client/graphql/index.js';
import { hasSportHubModuleEnabled } from '../../sport-hub/utils/moduleAccess.js';
import { useShlLeagueSelection } from './hooks/useShlLeagueSelection.js';
import '../client/scss/NavsAsideExtends.scss';

const CREATE_NEW_VALUE = '__create_new__';

const NavsAsideExtends = ({ User, i18n, t, ManageAsideView }) => {
  const history = useHistory();
  const location = useLocation();
  const { leagues, selectedLeagueId, setSelectedLeagueId } = useShlLeagueSelection();
  const defaultPrimaryLabelByLangRef = useRef({});
  const sportHubEnabled = hasSportHubModuleEnabled(User);
  const { data: sportsData } = useQuery(GET_SHL_SPORTS, {
    fetchPolicy: 'cache-first',
  });

  const translate = (key, fallback) => {
    if (typeof t === 'function') {
      const translated = t(key);
      if (translated && translated !== key) return translated;
    }
    const byI18n = i18n?.getResource?.(i18n.language, 'sport-hub-league', key);
    if (typeof byI18n === 'string' && byI18n !== key) return byI18n;
    return fallback;
  };

  const selectedLeague = useMemo(
    () => leagues.find((league) => String(league.id) === String(selectedLeagueId)) || null,
    [leagues, selectedLeagueId]
  );
  const sportLabelMap = useMemo(
    () =>
      (sportsData?.MDB?._SHL_?.sports || []).reduce((acc, sport) => {
        const key = String(sport?.id || '').toUpperCase();
        if (!key) return acc;
        acc[key] = sport?.label?.it || sport?.label?.en || sport?.id;
        return acc;
      }, {}),
    [sportsData]
  );
  const selectedSportLabel = selectedLeague?.sport
    ? sportLabelMap[String(selectedLeague.sport).toUpperCase()] || selectedLeague.sport
    : null;

  const syncPrimaryModuleDomLabel = (label) => {
    if (typeof document === 'undefined') return;
    const target = document.querySelector('#primaryModule a span');
    if (!target) return;
    target.textContent = label;
  };

  useEffect(() => {
    const lang = i18n?.language;
    if (!lang) return;

    if (!defaultPrimaryLabelByLangRef.current[lang]) {
      const base = i18n?.getResource?.(lang, 'nav', 'primaryMenu.sport-hub-league');
      defaultPrimaryLabelByLangRef.current[lang] = base || 'Sport Hub League';
    }

    const fallback = defaultPrimaryLabelByLangRef.current[lang] || 'Sport Hub League';
    const nextLabel = selectedLeague?.name || fallback;

    if (typeof i18n?.addResource === 'function') {
      i18n.addResource(lang, 'nav', 'primaryMenu.sport-hub-league', nextLabel);
    }

    syncPrimaryModuleDomLabel(nextLabel);
  }, [i18n, i18n?.language, selectedLeague]);

  const handleChange = (event) => {
    const value = event.target.value;
    if (value === CREATE_NEW_VALUE) {
      setSelectedLeagueId(null);
      if (!location.pathname.startsWith('/sport-hub-league/settings')) {
        history.push('/sport-hub-league/settings');
      }
    } else {
      setSelectedLeagueId(value);
    }
    if (typeof ManageAsideView === 'function') {
      ManageAsideView(false);
    }
  };

  const handleSportHubClick = () => {
    if (typeof ManageAsideView === 'function') {
      ManageAsideView(false);
    }
  };

  return (
    <div id="NavsAsideExtends_SHL">
      {sportHubEnabled && (
        <NavLink to="/sport-hub" className="shl-sport-hub-link" onClick={handleSportHubClick}>
          <FontAwesomeIcon className="mr-1" icon={faFutbol} />{' '}
          {translate('nav.sportHubLink', 'Sport Hub')}
        </NavLink>
      )}
      <div className="shl-league-selector-head">
        <label htmlFor="shl-league-selector" className="shl-league-selector-label">
          {translate('nav.leagueSelector.label', 'Campionato attivo')}
        </label>
        {selectedSportLabel && <span className="shl-league-sport-badge">{selectedSportLabel}</span>}
      </div>
      <div className="shl-league-selector-field">
        <select
          id="shl-league-selector"
          className="shl-league-selector-input"
          onChange={handleChange}
          value={selectedLeagueId || CREATE_NEW_VALUE}
        >
          {leagues.map((league) => (
            <option value={league.id} key={league.id}>
              {league.name}
            </option>
          ))}
          <option value={CREATE_NEW_VALUE}>
            {translate('nav.leagueSelector.createNew', '+ Crea nuovo campionato')}
          </option>
        </select>
        <span className="shl-league-selector-chevron" aria-hidden />
      </div>
    </div>
  );
};

NavsAsideExtends.propTypes = {
  User: PropTypes.object,
  i18n: PropTypes.object,
  t: PropTypes.func,
  ManageAsideView: PropTypes.func,
};

export default NavsAsideExtends;
